import styled from 'styled-components';
import React, { useState } from 'react';
import Transition from 'react-transition-group/Transition';
import {
  biggerThan,
  iphone5,
  smallerThanOrEqualTo,
} from '../../../../../../../core/util/styledComponents';
import Alert from '../../../../../../SolvPatternLibrary/Icons/Alert';
import { colors } from '../../../../../../../constants/colors';
import { fontFamily, fontSize } from '../../../../../../../constants/text';
import Button, { ButtonVariations } from '../../../../../../SolvPatternLibrary/Button';
import { safeGet } from '../../../../../../../core/util/object';
import { analyticsTrackEvent } from '../../../../../../../core/analytics';
import {
  ACCOUNT_INSURANCE_USER_ERROR_BANNER,
  ACTION_CTA_CLICK,
  ACTION_DISMISS,
  ACTION_SEE_TILE,
  PROFILES_TOUCH_ADD_INSURANCE,
} from '../../../../../../../core/analytics/events';
import history from '../../../../../../../core/history';
import {
  ELIGIBILITY_REJECT_REASON_CARRIER_DOWN,
  ELIGIBILITY_REJECT_REASON_MEMBER_NOT_FOUND,
} from '../../../../../../../constants/insurance';
import { fadeOutWithoutTranslating } from '../../../../../../../core/util/animations';
import Observer from '../../../../../../SolvPatternLibrary/Profilers/Observer';
import useSelectUserProfile from '../../../../../hooks/useSelectUserProfile';
import useSelectEditInsurance from '../../InsuranceCard/hooks/useSelectEditInsurance';
import useSelectHandleDismissed from '../hooks/useSelectHandleDismiss';

const TitleContainer = styled.div.withConfig({ componentId: 'sc-1fmqlzj-0' })`
  display: flex;
  align-items: center;
`;

const IconTextContainer = styled.div.withConfig({ componentId: 'sc-1fmqlzj-1' })`
  flex-grow: 1;
  ${biggerThan.md`
    display: flex;
    align-items: center;
  `}
`;

const IconWarning = styled(Alert).withConfig({ componentId: 'sc-1fmqlzj-2' })`
  height: 20px;
  width: 24px;
  ${smallerThanOrEqualTo.md`
    padding-top: 5px;
  `}
`;

const Root = styled.div.withConfig({ componentId: 'sc-1fmqlzj-3' })`
  background-color: ${colors.white};
  padding: 20px;
  ${smallerThanOrEqualTo.md`
    box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.04), 0 0 8px 0 rgba(0, 0, 0, 0.1);
    max-width: 375px;
  `}
`;

const Close = styled.div.withConfig({ componentId: 'sc-1fmqlzj-4' })`
  width: 19px;
  height: 19px;
  position: relative;
  cursor: pointer;
  margin: 0 0 -10px auto;
  ${smallerThanOrEqualTo.md`
    margin 0 0 0 auto;
  `}

  &:before {
    content: '';
    left: 50%;
    height: 100%;
    width: 1px;
    position: absolute;
    transform: rotate(45deg);
    background-color: ${colors.squid};
  }

  &:after {
    content: '';
    left: 50%;
    height: 100%;
    width: 1px;
    position: absolute;
    transform: rotate(-45deg);
    background-color: ${colors.squid};
  }
`;

const Header = styled.div.withConfig({ componentId: 'sc-1fmqlzj-5' })`
  display: flex;
  justify-content: space-between;
`;

const Title = styled.div.withConfig({ componentId: 'sc-1fmqlzj-6' })`
  color: ${colors.squid};
  font-family: ${fontFamily.bold};
  font-size: ${fontSize.default};
  line-height: 20px;
  ${iphone5`
    font-size: ${fontSize.mediumSmall};
  `};
  ${biggerThan.md`
    display: none;
  `}
`;

const Copy = styled.div.withConfig({ componentId: 'sc-1fmqlzj-7' })`
  color: ${colors.lightText};
  font-family: ${fontFamily.book};
  font-size: ${fontSize.mediumSmall};
  line-height: 20px;
  ${smallerThanOrEqualTo.md`
    margin-top: 9px;
  `}
  ${iphone5`
    font-size: ${fontSize.small};
  `};
  ${biggerThan.md`
    font-size: ${fontSize.default};
    padding-left: 12px;
  `}
`;

const StyledButton = styled(Button).withConfig({ componentId: 'sc-1fmqlzj-8' })`
  padding-left: 0;
  font-size: ${fontSize.mediumSmall};
  line-height: 20px;
  ${smallerThanOrEqualTo.md`
    margin-top: 10px;
  `}
  ${biggerThan.md`
    font-size: ${fontSize.default};
    padding-left: 12px;
  `}
  cursor: pointer;
`;

type Props = {
  userProfileId: string;
  insuranceProfile: any;
};

const InsuranceUserErrorBanner = (props: Props) => {
  const { userProfileId, insuranceProfile } = props;

  const insuranceProfileId = safeGet(insuranceProfile)('insurance_profile_id');
  const userProfile = useSelectUserProfile(userProfileId);
  const dispatchInsuranceForEdit = useSelectEditInsurance(userProfileId);
  const handleDismiss = useSelectHandleDismissed(userProfileId);
  let [bannerDismissed, setBannerDismissed] = useState(
    safeGet(insuranceProfile, false)('is_eligibility_rejection_error_dismissed') || false
  );

  const getRejectReasonType = () =>
    safeGet(insuranceProfile, false)('eligibility_reject_reason_type');

  const handleSawComponent = () => {
    analyticsTrackEvent(ACCOUNT_INSURANCE_USER_ERROR_BANNER, {
      action: ACTION_SEE_TILE,
      rejectReasonType: getRejectReasonType(),
    });
  };

  const handleUpdateInsurance = (event: any) => {
    event.stopPropagation();
    const userProfileId = userProfile.user_profile_id;
    const trackingProperties = { userProfileId, insuranceType: 'medical' };
    analyticsTrackEvent(PROFILES_TOUCH_ADD_INSURANCE, trackingProperties);
    analyticsTrackEvent(ACCOUNT_INSURANCE_USER_ERROR_BANNER, {
      userProfileId,
      action: ACTION_CTA_CLICK,
      rejectReasonType: getRejectReasonType(),
    });
    dispatchInsuranceForEdit();
    history.push(`/add-insurance/up?p=${userProfileId}`);
  };

  const shouldRenderBanner = () => {
    const eligibilityRejectReasonType = getRejectReasonType();
    return (
      !bannerDismissed &&
      (eligibilityRejectReasonType === ELIGIBILITY_REJECT_REASON_MEMBER_NOT_FOUND ||
        eligibilityRejectReasonType === ELIGIBILITY_REJECT_REASON_CARRIER_DOWN)
    );
  };

  const handleClose = () => {
    analyticsTrackEvent(ACCOUNT_INSURANCE_USER_ERROR_BANNER, {
      insuranceProfileId,
      action: ACTION_DISMISS,
      rejectReasonType: getRejectReasonType(),
    });
    setBannerDismissed(true);
    handleDismiss();
  };

  const renderNotFound = () => (
    <Root>
      <Header>
        <IconTextContainer>
          <TitleContainer>
            <Title>Please review your information.</Title>
          </TitleContainer>

          <Copy>Please double check your member name, ID, and birthday.</Copy>

          <StyledButton onClick={handleUpdateInsurance} variation={ButtonVariations.link}>
            Update my information
          </StyledButton>
        </IconTextContainer>

        <Close onClick={handleClose} />
      </Header>
    </Root>
  );

  const renderCarrierFound = () => (
    <Root>
      <Header>
        <IconTextContainer>
          <TitleContainer>
            <IconWarning />

            <Title>Carrier down</Title>
          </TitleContainer>

          <Copy>
            We were unable to load your information because{' '}
            {safeGet(insuranceProfile, 'your carrier')('insurer_name')} is currently down. Please
            try again later.
          </Copy>

          <StyledButton onClick={handleClose} variation={ButtonVariations.link}>
            Dismiss
          </StyledButton>
        </IconTextContainer>

        <Close onClick={handleClose} />
      </Header>
    </Root>
  );

  return (
    <Transition
      in={shouldRenderBanner()}
      mountOnEnter
      onExiting={(node) => fadeOutWithoutTranslating(node, { duration: 500 })}
      timeout={500}
      unmountOnExit
    >
      <Observer handleSawComponent={handleSawComponent}>
        {getRejectReasonType() === ELIGIBILITY_REJECT_REASON_MEMBER_NOT_FOUND && renderNotFound()}
        {getRejectReasonType() === ELIGIBILITY_REJECT_REASON_CARRIER_DOWN && renderCarrierFound()}
      </Observer>
    </Transition>
  );
};
export default InsuranceUserErrorBanner;
